import { useContext, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Lock, Save, ShieldQuestion } from "lucide-react";
import { StickySaveBar } from "@/components/StickySaveBar";
import { SettingsReadOnly, ViewOnlyPill, lockedProps } from "@/components/SettingsSection";
import { V } from "@/lib/vocab";

export default function Advanced({ embedded }) {
  const { tenantId, role } = useDash();
  const [adv, setAdv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Embedded in Settings: the "advanced" dial decides (SettingsReadOnly). Standalone page: super admins only.
  const lockReason = useContext(SettingsReadOnly);
  const readOnly = Boolean(lockReason) || (!embedded && role !== "super_admin");

  // Unsaved-changes guard: warn before the tab closes with un-saved dials.
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  useEffect(() => { api.get(`/tenants/${tenantId}`).then((r) => setAdv(r.data.advanced)).catch(() => {}); }, [tenantId]);

  if (!embedded && role !== "super_admin") {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 p-10 text-center max-w-md mx-auto flex flex-col items-center gap-3">
        <Lock className="h-8 w-8 text-sand-300" />
        <p className="font-heading font-bold">Managed by Redline</p>
        <p className="text-sm text-muted-foreground">
          Advanced accuracy dials (model, temperature, retrieval depth) are held by Redline super
          admins so one {V.lc("org")} can't quietly degrade answer quality. You can see them under Settings → Advanced.
        </p>
        <div className="flex flex-wrap gap-2 justify-center pt-1">
          <a data-testid="advanced-goto-settings" href="/dashboard/settings?tab=advanced"
            className="rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700">
            Open Settings
          </a>
          <a data-testid="advanced-request-change" href="/dashboard/settings?request=advanced"
            className="rounded-full border border-sand-200 text-sm font-semibold px-4 py-2 hover:border-ink">
            Request a change
          </a>
        </div>
      </div>
    );
  }
  if (!adv) return null;

  const upd = (k, v) => { if (readOnly) return; setAdv({ ...adv, [k]: v }); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/tenants/${tenantId}/advanced`, adv); toast.success("Advanced settings saved"); setDirty(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="advanced-dials">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {!embedded && <PageTitle title={<>Advanced settings <span className="text-xs bg-redline-50 text-redline-700 px-2.5 py-1 rounded-full font-bold">Super Admin</span></>} />}
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            {embedded && <span className="font-heading font-bold text-[15px] text-ink">Accuracy dials</span>}
            {readOnly && <ViewOnlyPill testid="advanced-view-only" reason={lockReason || "redline"} />}
            <span>Accuracy-critical dials. Kept low &amp; grounded by default.</span>
          </p>
        </div>
        {!readOnly && <button data-testid="advanced-save-btn" onClick={save} disabled={saving}
          className={`shrink-0 rounded-full font-semibold px-4 py-2 text-sm flex items-center gap-2 transition-colors disabled:opacity-60 ${dirty ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-redline-500 text-white hover:bg-redline-600"}`}>
          <Save className="h-4 w-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save"}
        </button>}
      </div>
      <StickySaveBar dirty={dirty} saving={saving} onSave={save} label="Advanced dials changed" testid="advanced-sticky" />

      <section className={`bg-white rounded-2xl border p-5 md:px-6 space-y-6 ${readOnly ? "rl-view-only border-sand-300" : "border-sand-200"}`} {...(readOnly ? lockedProps(lockReason || "redline") : {})}>
        <div className="rounded-xl bg-sand-50 border border-sand-100 px-4 py-3 text-xs text-muted-foreground" data-testid="adv-model-note">
          The concierge AI model is chosen per {V.lc("org")} under <span className="font-semibold text-ink">The Concierge → Base AI Model</span> — you can run OpenAI (GPT), Anthropic (Claude) or Google (Gemini). That picker is the single source of truth — these accuracy dials apply on top of whichever model is selected there.
        </div>

        <div>
          <div className="flex justify-between text-sm font-semibold"><span>Temperature</span><span className="text-redline-600">{Number(adv.temperature).toFixed(2)}</span></div>
          <Slider data-testid="adv-temperature" disabled={readOnly} min={0} max={1} step={0.05} value={[adv.temperature]} onValueChange={([v]) => upd("temperature", v)} className="mt-3" />
          <p className="text-xs text-muted-foreground mt-1">Kept low (~0.2) for grounded accuracy.</p>
        </div>

        <div>
          <div className="flex justify-between text-sm font-semibold"><span>Retrieved chunks (top-k)</span><span className="text-redline-600">{adv.chunk_count}</span></div>
          <Slider data-testid="adv-chunk-count" disabled={readOnly} min={1} max={10} step={1} value={[adv.chunk_count]} onValueChange={([v]) => upd("chunk_count", v)} className="mt-3" />
        </div>

        <div>
          <div className="flex justify-between text-sm font-semibold"><span>Confidence threshold</span><span className="text-redline-600">{Number(adv.confidence_threshold).toFixed(2)}</span></div>
          <Slider data-testid="adv-confidence" disabled={readOnly} min={0} max={0.5} step={0.01} value={[adv.confidence_threshold]} onValueChange={([v]) => upd("confidence_threshold", v)} className="mt-3" />
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><ShieldQuestion className="h-3 w-3" /> Below this, the bot escalates instead of guessing.</p>
        </div>

        <div>
          <div className="flex justify-between text-sm font-semibold"><span>Rate limit (msgs / min / number)</span><span className="text-redline-600">{adv.rate_limit_per_min}</span></div>
          <Slider data-testid="adv-rate-limit" disabled={readOnly} min={5} max={60} step={5} value={[adv.rate_limit_per_min]} onValueChange={([v]) => upd("rate_limit_per_min", v)} className="mt-3" />
        </div>
      </section>
    </div>
  );
}
